import { useEffect, useState } from 'react';

import moment from 'moment/moment';

import { useUserStrategies } from '../../../context/UserStrategies';

import {
  INTERVALS,
  TInterval,
  useIntervals,
} from '../../../hooks/useIntervals';

import { Checkbox, DatePicker, DropdownMenu } from '../../form';

import {
  computePercentage,
  getStartEndDates,
} from '../../../helpers/functions';

import { AssetPerformanceModel } from './AssetPerformance.model';
import { ApexChart, TApexChartSeries } from './ApexChart';
import { Icon, Loading } from '../../UI';
import { StrategyLabel } from '../../Strategies/StrategyLabel';
import { CHART_COLORS_HEX, COLORS } from '../../../constants/colors';
import ScrollArrows from '../Contribution/ScrollArrows';
import { useToggle } from '../../../hooks/useToggle';
import { useApolloClient } from '@apollo/client';

import { useTranslation } from 'react-i18next';
import { TDateISO } from '../../../types/date';
import CalendarRange from '../../CalendarRange/CalendarRange';
import { useErrorHandler } from 'react-error-boundary';
import { IAsset, TSeries, TTicker } from '../../../types/model';
import { GET_ASSET_HISTORY } from '../../../http/apollo-queries';
import { logger } from '../../../helpers/logger';
import { getPrecision, numberOutput } from '../../../helpers/calc';
import SearchAsset from '../SearchAsset';
import { Debug } from '../../Debug/Debug';
import { CurrencyCodeEnum } from '../../../constants/enums';
import { Range, RangeKeyDict } from 'react-date-range';

const chartTypes = ['chartLine', 'chartCandleStick'] as const;

export type TCandleStickData = {
  close: number;
  high: number;
  low: number;
  open: number;
  datetime: TDateISO;
};

export type TLineData = {
  close: number;
  datetime: TDateISO;
};

export type THistoriesMap = {
  [key: string]: TSeries[];
};

const AssetPerformanceWidget = ({
  asset,
  width,
  height,
  strategyId = '',
  setLegend,
  standAlone,
}: AssetPerformanceModel) => {
  const { t } = useTranslation();
  const onError = useErrorHandler();
  const client = useApolloClient();

  const { strategies } = useUserStrategies();
  const { interval, IntervalSelector, handleInterval } = useIntervals(
    ['VAR_24H', 'VAR_7D', 'VAR_30D', 'VAR_1Y'],
    'VAR_30D'
  );

  const {
    toggleState: isBlackSwanToggleActive,
    ToggleComponent: BlackSwanToggle,
    handleToggleState: setBlackSwanToggleState,
  } = useToggle({ description: t('widget.blackSwan') });

  const {
    toggleState: isPriceTargetToggleActive,
    ToggleComponent: PriceTargetToggle,
    handleToggleState: setPriceTargetState,
  } = useToggle({ description: t('strategies.priceTarget') });

  const strategy = strategies.find((s) => s.id === strategyId);
  const strategyAssets = [...(strategy?.strategyAssets?.items ?? [])];
  const [[startDate, endDate], setDates] = useState<Date[]>([]);

  const getStrategyAssetWithHigherValue = () => {
    return strategyAssets.sort(
      (a, b) =>
        b.unit * b.asset.history[0].usd - a.unit * a.asset.history[0].usd
    )[0];
  };

  const [selectedAssets, setSelectedAssets] = useState<IAsset[]>([
    asset || getStrategyAssetWithHigherValue().asset,
  ]);

  const [chartType, setChartType] =
    useState<(typeof chartTypes)[number]>('chartLine');

  const [isLoading, setIsLoading] = useState(false);
  const [isDateSearch, setIsDateSearch] = useState(false);
  const [showCalendar, setShowCalendar] = useState(false);
  const [dateRangeState, setDateRangeState] = useState<Range[]>([
    {
      startDate: moment().subtract(1, 'week').toDate(),
      endDate: moment().toDate(),
      key: 'selection',
    },
  ]);

  const [assetHistories, setAssetHistories] = useState<THistoriesMap>({});

  const { currency = CurrencyCodeEnum.USD } = selectedAssets[0];

  const handleDateRangePickerChange = (
    item: RangeKeyDict,
    dateSearch: boolean
  ) => {
    const { selection } = item;
    setDateRangeState([selection]);
    setDates([selection.startDate!, selection.endDate!]);
    setIsDateSearch(dateSearch);
  };

  useEffect(() => {
    const { endDate, startDate } = getStartEndDates(interval);

    logger.log(
      'AssetPerformance:effect:interval',
      interval,
      startDate.toISOString(),
      endDate.toISOString()
    );
    setBlackSwanToggleState(false);
    handleDateRangePickerChange(
      {
        selection: { startDate, endDate, key: 'selection' },
      },
      false
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval]);

  const onChooseRange = async (item) => {
    if (item['0'] && item['1'] && moment(item[0]).diff(item[1], 'days')) {
      setDates([
        moment(item['0']).add(1, 'day').toDate(),
        moment(item['1']).add(1, 'day').toDate(),
      ]);
    }
  };

  const extractDataForCandleStick = (
    data: TSeries[],
    currency: keyof TTicker
  ): TCandleStickData[] => {
    // Group the data by date
    const groupedData = data.reduce((acc, item) => {
      const date = new Date(item.date).toISOString().slice(0, 10);
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(item);
      return acc;
    }, {});

    // Extract high, low, last, first for each day
    return Object.keys(groupedData).map((date) => {
      const prices = groupedData[date].map((item) => item[currency]);
      const high = Math.max(...prices);
      const low = Math.min(...prices);
      const close = groupedData[date][0][currency];
      const open = groupedData[date][groupedData[date].length - 1][currency];
      const datetime = date as TDateISO;
      return { datetime, high, low, open, close };
    });
  };

  const extractDataForLine = (
    data: TSeries[],
    currency: keyof TTicker,
    interval: TInterval
  ): TLineData[] => {
    let groupedData: Record<string, TSeries[]>;

    if (interval === 'VAR_24H') {
      const timeframe = 30;
      let daysAdded = new Set<string>();
      groupedData = data.reduce((acc, item) => {
        const itemMoment = moment(item.date);

        if (daysAdded.size <= 2 && Object.keys(acc).length <= 48) {
          const dateKey = itemMoment
            .startOf('minute')
            .minute(Math.floor(itemMoment.minute() / timeframe) * timeframe)
            .format('YYYY-MM-DD HH:mm');

          if (!acc[dateKey]) {
            daysAdded.add(itemMoment.format('YYYY-MM-DD'));
            acc[dateKey] = [item];
          }
        }
        return acc;
      }, {});
    } else {
      // Group by day
      groupedData = data.reduce((acc, item) => {
        const date = new Date(item.date).toISOString().slice(0, 10);
        if (!acc[date]) {
          acc[date] = [];
        }
        acc[date].push(item);
        return acc;
      }, {});
    }
    return Object.keys(groupedData).map((date) => {
      const close = groupedData[date][0][currency];
      const datetime = date as TDateISO;
      return { datetime, close };
    });
  };

  const { dropdownItems, dropdownItemsData } = asset
    ? { dropdownItems: [], dropdownItemsData: [] }
    : strategyAssets
        .filter(({ id }) => !selectedAssets.find((asset) => asset.id === id))
        .reduce(
          ({ dropdownItems, dropdownItemsData }, { name, asset }) => {
            dropdownItems.push(name);
            dropdownItemsData.push(asset);

            return { dropdownItems, dropdownItemsData };
          },
          { dropdownItems: [] as string[], dropdownItemsData: [] as IAsset[] }
        );

  const handleChoose = (asset) => {
    logger.log('AssetPerformance:handleChoose', asset);

    setSelectedAssets((previousAssets) => {
      const exist = previousAssets.find(({ id }) => asset.id === id);
      logger.log(
        'AssetPerformance:handleChoose:previousAssets',
        !!exist,
        exist?.id,
        previousAssets.map(({ id }) => id)
      );

      if (exist && previousAssets.length === 1) {
        return previousAssets;
      }

      return !exist
        ? [...previousAssets, asset]
        : previousAssets.filter(({ id }) => asset.id !== id);
    });
  };

  const isDropdownDisabled = selectedAssets.length > 4;

  useEffect(() => {
    if (!startDate || !endDate) {
      return;
    }

    logger.log('AssetPerformance:selectedAssets', selectedAssets.length);

    setIsLoading(true);

    Promise.all(
      selectedAssets.map(({ id }) => {
        logger.log(
          'AssetPerformance:query',
          id,
          moment(startDate).format('YYYY-MM-DD'),
          moment(endDate).format('YYYY-MM-DD'),
          interval
        );

        return client
          .query({
            query: GET_ASSET_HISTORY,
            variables: {
              startDate: moment(startDate).format('YYYY-MM-DD'),
              endDate: moment(endDate).format('YYYY-MM-DD'),
              assetId: id,
              last: !isDateSearch,
            },
            fetchPolicy: 'cache-first',
          })
          .then(({ data: { getAssetHistory: history = [] } = {} }) => {
            logger.log('AssetPerformance:getAssetHistory', id, history);

            return {
              [id]: [...history].reverse() as TSeries[],
            };
          });
      })
    )
      .then((res) => {
        logger.log('AssetPerformance:res', res);

        const histories = res.reduce((acc, item) => ({ ...acc, ...item }), {});

        // logger.log('AssetPerformance:histories', histories);

        setAssetHistories(histories);
      })
      .catch(onError)
      .finally(() => setIsLoading(false));
  }, [selectedAssets, startDate, endDate]);

  useEffect(() => {
    setLegend?.(
      width === 1 ? undefined : (
        <div
          className={`
          flex
          border-l
          border-grey-10
          dark:border-grey-70
          px-3
          h-full
          ${!asset && `border-r mr-6 gap-3 divide-x divide-grey-10`}
        `}
        >
          <BlackSwanToggle />
          {!asset && <PriceTargetToggle className="pl-3" />}
        </div>
      )
    );

    return () => setLegend?.(undefined);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBlackSwanToggleActive, isPriceTargetToggleActive, width]);

  const filteredStrategyAssets = strategyAssets.filter(({ assetId }) =>
    selectedAssets.find((asset) => asset.id === assetId)
  );

  const targets = asset
    ? []
    : filteredStrategyAssets.reduce(
        (
          a,
          { asset: { name }, strategyAssetTargets: { items }, entryPrice },
          colorIdx,
          arr
        ) => [
          ...a,
          ...(items.length > 0
            ? [
                {
                  name,
                  y:
                    arr.length > 1
                      ? Math.abs(
                          computePercentage(
                            entryPrice,
                            items[0].stopProfit,
                            100
                          )
                        )
                      : +items[0].stopProfit.toFixed(2),
                  colorIdx,
                },
                {
                  name,
                  y:
                    arr.length > 1
                      ? computePercentage(entryPrice, items[0].stopLoss, 100)
                      : +items[0].stopLoss.toFixed(2),
                  colorIdx,
                },
              ]
            : []),
        ],
        [] as {
          y: number;
          colorIdx: number;
          name: string;
        }[]
      );

  const series: TApexChartSeries[] = selectedAssets.reduce(
    (acc: TApexChartSeries[], { name, id }, i, arr) => {
      const assetHistory = assetHistories[id] || [];

      logger.log('AssetPerformance:assetHistory', assetHistory);
      let chartData: { x: number; y: number | number[] }[] = [];

      let rawData: number[][] = [];
      let rawPercentage: number[][] = [];

      if (chartType === 'chartCandleStick' && i === 0) {
        const candleStickHistory = extractDataForCandleStick(
          assetHistory,
          arr.length > 1 || arr[0].type === 'FOREX' ? 'usd' : 'cur'
        );

        chartData = candleStickHistory.map(
          ({ close, high, low, open, datetime }, _, dayValues) => {
            const dayToCompare =
              i === 0 ? candleStickHistory.slice(-1)[0] : dayValues[0];

            const d = [open, high, low, close].map((el) => numberOutput(el));
            rawData.push(d);
            rawPercentage.push([
              computePercentage(+dayToCompare.open, +open, 100),
              computePercentage(+dayToCompare.high, +high, 100),
              computePercentage(+dayToCompare.low, +low, 100),
              computePercentage(+dayToCompare.close, +close, 100),
            ]);

            return {
              x: new Date(datetime).getTime(),
              y:
                arr.length > 1
                  ? [
                      computePercentage(+dayToCompare.open, +open, 100),
                      computePercentage(+dayToCompare.high, +high, 100),
                      computePercentage(+dayToCompare.low, +low, 100),
                      computePercentage(+dayToCompare.close, +close, 100),
                    ]
                  : [open, high, low, close].map((el) => numberOutput(el)),
            };
          }
        );
      } else {
        const lineHistory = extractDataForLine(
          assetHistory,
          arr.length > 1 || arr[0].type === 'FOREX' ? 'usd' : 'cur',
          interval
        );

        logger.log('AssetPerformance:lineHistory', lineHistory);

        chartData = lineHistory.map(({ datetime, close }, _, dayValues) => {
          const dayToCompare = i === 0 ? lineHistory.slice(0)[0] : dayValues[0];

          rawData.push([numberOutput(close)]);
          rawPercentage.push([
            computePercentage(dayToCompare.close, close, 100),
          ]);

          return {
            x: new Date(datetime).getTime(),
            y:
              arr.length > 1
                ? computePercentage(close, dayToCompare.close, 100)
                : numberOutput(close),
          };
        });
      }

      // logger.log('AssetPerformance:lineChartData', lineChartData);
      // logger.log('AssetPerformance:candleStickChartData', candleStickChartData);

      return [
        ...acc,
        {
          name,
          type: !i && chartType === 'chartCandleStick' ? 'candlestick' : 'line',
          data: chartData,
          rawData,
          rawPercentage,
        },
      ];
    },
    []
  );

  // logger.log('AssetPerformance:series', series);

  const dropdownItemsWidth1 = [
    <div
      className="flex items-center gap-3 w-48"
      onClick={() => setChartType('chartLine')}
    >
      <Icon svgName="chartLine" />
      <span className="mr-auto capitalize-first">
        {t('common.line', { value: t('common.graph') })}
      </span>
      {chartType === 'chartLine' && (
        <Checkbox shape="circle" isCheck={true} size="small" />
      )}
    </div>,
    {
      item: (
        <div
          className="flex items-center gap-3 w-full"
          onClick={() => setChartType('chartCandleStick')}
        >
          <Icon svgName="chartCandleStick" />
          <span className="mr-auto capitalize-first">
            {t('common.candle', { value: t('common.stick') })}
          </span>
          {chartType === 'chartCandleStick' && (
            <Checkbox shape="circle" isCheck={true} size="small" />
          )}
        </div>
      ),
      withBorder: true,
    },
    {
      item: interval,
      hideItemOnClick: false,
      dropdownItems: [...INTERVALS],
      handleClick: handleInterval,
    },
    {
      item: (
        <div
          className="flex items-center gap-3 w-full"
          onClick={() => setShowCalendar((pr) => !pr)}
        >
          <Icon svgName="cal" />
          <span className="mr-auto capitalize-first">
            {t('common.custom', { value: t('common.date') })}
          </span>
        </div>
      ),
      withBorder: true,
    },

    <div
      className="flex items-center gap-3 w-full"
      onClick={() => setBlackSwanToggleState()}
    >
      <span className="mr-auto">{t('widget.blackSwan')}</span>
      {isBlackSwanToggleActive && (
        <Checkbox shape="circle" isCheck={true} size="small" />
      )}
    </div>,
    <div
      className="flex items-center gap-3 w-full"
      onClick={() => setPriceTargetState()}
    >
      <span className="mr-auto">{t('strategies.priceTarget')}</span>
      {isPriceTargetToggleActive && (
        <Checkbox shape="circle" isCheck={true} size="small" />
      )}
    </div>,
  ];

  return (
    <div className={`flex flex-col h-full w-full dark:bg-grey-90`}>
      {showCalendar ? (
        <CalendarRange
          goBack={() => setShowCalendar(false)}
          handleDateRangeChange={(item) =>
            handleDateRangePickerChange(item, true)
          }
          ranges={dateRangeState}
          startDate={startDate}
          endDate={endDate}
        />
      ) : (
        <>
          <div
            className={`
              flex
              w-full
              h-16
              items-center
              pl-5
              border-b
              border-grey-10
              dark:border-grey-70
            `}
          >
            {asset ? (
              <StrategyLabel
                className={'ml-3'}
                key={asset.id}
                color={Object.keys(CHART_COLORS_HEX)[0]}
                name={asset.name}
                viewType={'simple'}
              />
            ) : (
              <>
                <div className="flex w-auto items-center gap-2 flex-shrink-0">
                  <SearchAsset
                    onChoose={handleChoose}
                    title={t('widget.assetPerformance.addAsset')}
                    isMultiple={true}
                    isDisabled={selectedAssets.length === 5}
                  />
                </div>
                <ScrollArrows className="px-2 w-full">
                  <div className="flex w-full">
                    {selectedAssets.map((asset, i) => (
                      <StrategyLabel
                        className={'ml-3'}
                        key={asset.id}
                        color={Object.keys(COLORS)[i]}
                        name={asset.name}
                        viewType={selectedAssets.length > 1 ? 'tag' : 'simple'}
                        onClose={() => handleChoose(asset)}
                      />
                    ))}
                  </div>
                </ScrollArrows>
              </>
            )}

            {width !== 1 ? (
              <>
                <div className="flex h-full ml-auto w-min mr-3 border-r border-grey-10 ">
                  {chartTypes.map((svgName) => (
                    <div
                      className={`
                        flex
                        border-l
                        border-grey-10
                        h-full
                        ${
                          svgName === chartType
                            ? 'bg-grey-10 dark:bg-grey-50 pointer-events-none'
                            : 'cursor-pointer'
                        }
                      `}
                      key={svgName}
                      onClick={() => setChartType(svgName)}
                    >
                      <Icon
                        svgName={svgName}
                        className="mx-5 m-auto dark:text-white"
                        canUseDarkMode={false}
                      />
                    </div>
                  ))}
                </div>

                <div className="flex w-52">
                  <DatePicker
                    dateRangeMonths={width === 1 ? 1 : 2}
                    dropdownTooltipXOffset={-110}
                    dropdownTooltipYOffset={5}
                    endDate={endDate}
                    isRangePicker={true}
                    id={'assetPerformanceDate'}
                    hasBorderBottom={false}
                    maxDate={new Date()}
                    size={'medium'}
                    startDate={startDate}
                    onChange={onChooseRange}
                  />
                </div>

                <IntervalSelector
                  className={`
                    ml-3
                    flex-none
                    border-l
                    border-grey-10
                    dark:border-grey-70
                  `}
                />
              </>
            ) : (
              <DropdownMenu
                dropdownClassName={`
                  flex
                  items-center
                  border-l
                  border-grey-10
                  cursor-pointer
                  hover:border-black
                  hover:border
                  dark:border-grey-70
                  dark:hover:border-grey-30
                `}
                dropdownTooltipClassName={`
                  bg-white
                  pl-0
                  border-solid
                  border
                  border-grey-30
                `}
                dropdownArrowClassName="-right-5 bottom-1.5 text-black"
                dropdownTooltipXOffset={-100}
                dropdownTooltipYOffset={-15}
                dropdownItems={dropdownItemsWidth1}
              >
                <Icon svgName="options" className="w-12 h-12 py-4 pr-3" />
              </DropdownMenu>
            )}
          </div>
          <Debug>
            <>
              {selectedAssets.map(
                (asset) =>
                  `ID: ${asset?.id} - Symbol: ${asset?.symbol} Exchange: ${
                    asset?.exchange
                  } - Series: ${assetHistories?.[asset?.id]?.length}`
              )}
            </>
          </Debug>
          <div
            className={`w-full relative h-${
              height === 1 ? '80' : '240'
            } overflow-hidden`}
          >
            {isLoading && (
              <div
                className={
                  'z-10 bg-white dark:bg-black flex justify-center align-middle absolute left-0 top-0 bottom-0 right-0'
                }
              >
                <Loading status={true} />
              </div>
            )}
            {series.length > 0 ? (
              <>
                {/*<Debug>*/}
                {/*  <>{`values: ${JSON.stringify(series?.[0]?.rawData || 0)}`}</>*/}
                {/*</Debug>*/}
                <ApexChart
                  canShowBlackSwan={isBlackSwanToggleActive}
                  canShowPriceTargets={isPriceTargetToggleActive}
                  series={series}
                  currency={currency}
                  isLoading={isLoading}
                  chartType={
                    chartType === 'chartCandleStick' ? 'candlestick' : 'line'
                  }
                  targets={targets}
                  interval={interval}
                  setBlackSwanToggleState={setBlackSwanToggleState}
                  precision={getPrecision(series?.[0]?.rawData?.[0]?.[0] || 0)}
                />
              </>
            ) : (
              <div className="container mx-auto px-4 py-4 text-center">
                No data available! <br />
                Change time interval or search for another asset.
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default AssetPerformanceWidget;
